import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", city: "", service: "", message: "" };

export const FinalCTA = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/estimates`, form);
      setDone(true);
      toast.success("Request sent! We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="bg-charcoal py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <span className="text-brick-light font-bold text-sm tracking-[0.18em] uppercase">Free Estimate</span>
            <h2 className="mt-3 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03]">
              Let's talk about your project.
            </h2>
            <p className="mt-5 text-white/60 text-base sm:text-lg leading-relaxed max-w-lg">
              Tell us what you need and we'll get back to you with a free, no-obligation estimate. Prefer to talk? Give us a call — we'd love to help.
            </p>

            <div className="mt-10 space-y-5">
              <a href={COMPANY.phoneHref} data-testid="cta-phone-link" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-brick flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Call us</p>
                  <p className="font-heading font-bold text-white text-lg group-hover:text-brick-light transition-colors">{COMPANY.phoneDisplay}</p>
                </div>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="cta-email-link" className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Email</p>
                  <p className="font-heading font-bold text-white text-lg group-hover:text-brick-light transition-colors break-all">{COMPANY.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </span>
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-wide">Hours</p>
                  <p className="font-heading font-bold text-white text-lg">{COMPANY.hours}</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-[0_20px_60px_rgb(0,0,0,0.3)]">
              {done ? (
                <div data-testid="estimate-success" className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-brick mx-auto" />
                  <h3 className="mt-5 font-heading font-extrabold text-charcoal text-2xl">Thank you!</h3>
                  <p className="mt-2 text-charcoal/60">Your estimate request was received. We'll reach out to you soon.</p>
                  <button onClick={() => setDone(false)} className="mt-6 rounded-full bg-lightgray hover:bg-stoneaccent px-6 py-3 font-bold text-charcoal transition-colors">
                    Send another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="estimate-form" className="space-y-4">
                  <h3 className="font-heading font-extrabold text-charcoal text-2xl">Request a Free Estimate</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name *" testid="form-name">
                      <input required value={form.name} onChange={update("name")} data-testid="input-name" className="input" placeholder="Your name" />
                    </Field>
                    <Field label="Phone *" testid="form-phone">
                      <input required value={form.phone} onChange={update("phone")} data-testid="input-phone" className="input" placeholder="(616) 000-0000" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email" testid="form-email">
                      <input type="email" value={form.email} onChange={update("email")} data-testid="input-email" className="input" placeholder="you@email.com" />
                    </Field>
                    <Field label="City" testid="form-city">
                      <input value={form.city} onChange={update("city")} data-testid="input-city" className="input" placeholder="e.g. Grand Rapids" />
                    </Field>
                  </div>
                  <Field label="Service needed" testid="form-service">
                    <select value={form.service} onChange={update("service")} data-testid="input-service" className="input bg-white">
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>{s.title}</option>
                      ))}
                      <option value="Other">Other / Not sure</option>
                    </select>
                  </Field>
                  <Field label="Project details" testid="form-message">
                    <textarea value={form.message} onChange={update("message")} data-testid="input-message" rows={4} className="input resize-none" placeholder="Tell us about your project..." />
                  </Field>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="estimate-submit"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brick hover:bg-brick-dark disabled:opacity-60 text-white font-bold text-base px-8 py-4 transition-[background-color,transform] duration-200 hover:scale-[1.01]"
                  >
                    {loading ? "Sending..." : <>Request Free Estimate <Send className="w-4 h-4" /></>}
                  </button>
                  <p className="text-center text-charcoal/40 text-xs">We'll never share your information.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgba(34,34,34,0.12);
          background: #F5F5F5;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          color: #222222;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .input:focus {
          border-color: #8B2E1A;
          box-shadow: 0 0 0 3px rgba(139,46,26,0.12);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-charcoal/70 text-sm font-semibold mb-1.5">{label}</span>
    {children}
  </label>
);
